<template>
  <div ref="grid" class="tce-grid">
    <tce-grid-tabs
      v-if="tabs.length"
      :tabs="tabs"
      :analytics="analytics.tabs"
      @tab-updated="setTab"
    />
    <div id="panel" role="tabpanel">
      <tce-grid-sort
        v-if="shouldShowSort"
        :sort="sort"
        :sort-by-dictionary="sortByDictionary"
        :analytics="analytics.sort"
        @sort-updated="setSort"
      />
      <ul class="tce-grid__list">
        <slot v-if="!data" />
        <template v-else>
          <li
            v-for="(item, index) in data"
            :key="index"
            :data-index="index"
            class="tce-grid__item tce-grid__item--loaded"
          >
            <div class="tce-product-card">
              <div
                class="tce-product-card__background"
                :style="`background: linear-gradient(138deg, rgba(245, 231, 232, 0.20) 12.83%, ${item.GradientStartColor} 99.26%),
              linear-gradient(150deg, rgba(234, 234, 234, 0.30) 7.74%, ${item.GradientEndColor} 92.26%)`"
              >
                <div class="tce-product-card__background-texture"></div>
              </div>
              <div v-if="item.productLabel" class="tce-product-card__label">
                {{ item.productLabel }}
              </div>
              <img
                class="tce-product-card__image"
                :src="item.ProductImageSrc"
                :alt="item.ProductImageAltText"
              />
              <div class="tce-product-card__info">
                <h2 class="tce-product-card__name">
                  <a
                    v-tag:productclick="{
                      action: item.tagging.action,
                      label: item.tagging.label,
                      category: item.tagging.category,
                      list: item.tagging.list,
                      products: filteredDimensions(item.tagging.products),
                    }"
                    :href="item.link"
                    class="tce-product-card__link"
                  >
                    {{ item.productName }}
                  </a>
                </h2>
                <span class="tce-product-card__shades-amount">{{ item.ShadeAmount }}</span>
                <span class="tce-product-card__price">{{ item.price }}</span>
              </div>
            </div>
          </li>
        </template>
        <template v-if="lazyLoadData">
          <li
            v-for="(item, index) in lazyLoadData"
            :key="index"
            class="tce-grid__item tce-grid__item tce-grid__item--lazyloaded"
          >
            <div class="tce-product-card">
              <div
                class="tce-product-card__background"
                :style="`background: linear-gradient(138deg, rgba(245, 231, 232, 0.20) 12.83%, ${item.GradientStartColor} 99.26%),
              linear-gradient(150deg, rgba(234, 234, 234, 0.30) 7.74%, ${item.GradientEndColor} 92.26%)`"
              >
                <div class="tce-product-card__background-texture"></div>
              </div>
              <div v-if="item.productLabel" class="tce-product-card__label">
                {{ item.productLabel }}
              </div>
              <img
                class="tce-product-card__image"
                :src="item.ProductImageSrc"
                :alt="item.ProductImageAltText"
              />
              <div class="tce-product-card__info">
                <h2 class="tce-product-card__name">
                  <a
                    v-tag:productclick="{
                      action: item.tagging.action,
                      label: item.tagging.label,
                      category: item.tagging.category,
                      list: item.tagging.list,
                      products: filteredDimensions(item.tagging.products),
                    }"
                    :href="item.link"
                    class="tce-product-card__link"
                  >
                    {{ item.productName }}
                  </a>
                </h2>
                <span class="tce-product-card__shades-amount">{{ item.ShadeAmount }}</span>
                <span class="tce-product-card__price">{{ item.price }}</span>
              </div>
            </div>
          </li>
        </template>
      </ul>
      <tce-grid-pagination
        v-if="shouldShowPagination"
        :pagination="currentPagination"
        :pagination-dictionary="paginationDictionary"
        @page-updated="setPage"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'tce-grid',
};
</script>

<script setup>
import { tag } from '../../directives/analyticsHandler/tag';
import { AnalyticsHandler } from '../../directives/analyticsHandler';
import { computed, nextTick, onMounted, ref, watch, watchEffect } from 'vue';
import TceGridTabs from './TceGridTabs.vue';
import TceGridPagination from './TceGridPagination.vue';
import TceGridSort from './TceGridSort.vue';

const MOBILE_SCREEN_END = 768;
const LAZY_LOAD_THRESHOLD = 100;
const GRIDT_ITEM_SELECTOR = '.tce-grid__item';
const GRIDT_ITEM_LOADED_SELECTOR = '.tce-grid__item--loaded';
const GRIDT_ITEM_LAZYLOADED_SELECTOR = '.tce-grid__item--lazyloaded';

defineOptions({
  directives: {
    tag,
  },
});

const props = defineProps({
  tabs: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    default: () => {},
  },
  paginationDictionary: {
    type: Object,
    default: () => {},
  },
  sort: {
    type: Array,
    default: () => [],
  },
  sortByDictionary: { type: String, default: 'Sort by' },
  apiUrl: { type: String, default: '' },
  currentPageTemplate: { type: String, default: '' },
  currentPage: { type: String, default: '' },
  analytics: {
    type: Object,
    default: () => {},
  },
});

const grid = ref(null);
const data = ref(null);
const lazyLoadData = ref([]);
const activeTab = ref('');
const currentPagination = ref(props.pagination);
const activeSort = ref('');
const isLazyFetchEnabled = ref(true);
const gridItemsLength = ref(0);

const isMobile = computed(() => {
  return window.innerWidth < MOBILE_SCREEN_END;
});

const shouldShowPagination = computed(() => {
  return !isMobile.value && currentPagination.value?.total > 1;
});

const shouldShowSort = computed(() => {
  return props.sort.length && gridItemsLength.value;
});

watch(data, async () => {
  await nextTick();

  impressionTaggingObserver(GRIDT_ITEM_LOADED_SELECTOR);
});

watch(lazyLoadData, async () => {
  await nextTick();

  impressionTaggingObserver(GRIDT_ITEM_LAZYLOADED_SELECTOR);
});

watchEffect(() => {
  activeTab.value = props.tabs.find((tab) => tab.active) || null;
  activeSort.value = props.sort.find((sortItem) => sortItem.active) || null;
});

function setTab(data) {
  activeTab.value = data;
  currentPagination.value.page = 1;

  fetchGrid();
}

function setPage(data) {
  currentPagination.value.page = data;

  fetchGrid();
}

function setSort(data) {
  activeSort.value = data;
  currentPagination.value.page = 1;

  fetchGrid();
}

function requestUrl() {
  const queryParams = new URLSearchParams();
  queryParams.set('currentpagetemplate', props.currentPageTemplate);
  if (props.tabs.length) queryParams.set('category', activeTab.value.name);
  queryParams.set('page', currentPagination.value.page);
  queryParams.set('currentpage', props.currentPage);
  if (shouldShowSort.value) queryParams.set('sort', activeSort.value.key);

  return `${props.apiUrl}?${queryParams.toString()}`;
}

async function fetchGrid() {
  lazyLoadData.value = [];

  try {
    const response = await fetch(`/${requestUrl()}`);
    const responseData = await response.json();

    data.value = responseData.Data.Products.map((item) => {
      return { ...item, tagging: JSON.parse(item.tagging) };
    });
    gridItemsLength.value = data.value.length;
    currentPagination.value = responseData.Pagination;
  } catch (err) {
    console.error(err);
  }
}

async function fetchLazyGrid() {
  isLazyFetchEnabled.value = false;
  try {
    const response = await fetch(`/${requestUrl()}`);
    const responseData = await response.json();
    const iterableData = responseData.Data.Products.map((item) => {
      return { ...item, tagging: JSON.parse(item.tagging) };
    });
    lazyLoadData.value.push(...iterableData);
    currentPagination.value = responseData.Pagination;
    isLazyFetchEnabled.value = true;
  } catch (err) {
    console.error(err);
    isLazyFetchEnabled.value = true;
  }
}

function handleScroll() {
  const element = grid.value;

  if (
    element.getBoundingClientRect().bottom - LAZY_LOAD_THRESHOLD < window.innerHeight &&
    currentPagination.value.page < currentPagination.value.total &&
    isLazyFetchEnabled.value
  ) {
    currentPagination.value.page++;

    fetchLazyGrid();
  }
}

function filteredDimensions(products) {
  return products.map((product) => {
    const { dimension38, dimension39, dimension59, ...rest } = product;
    return rest;
  });
}

function impressionTaggingObserver(selector) {
  if (!data.value || !lazyLoadData.value) return;

  const slides = grid.value.querySelectorAll(selector);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const tag = {
            type: 'productImpression',
            name: 'view_item_list',
            products: data.value[entry.target.dataset.index].tagging.products,
            currency: data.value[entry.target.dataset.index].tagging.currency,
          };
          try {
            AnalyticsHandler.getAnalyticsHandler().push(tag);
          } catch (e) {
            /* istanbul ignore next */
            console.warn('Could not push to dataLayer', tag);
          }

          observer.unobserve(entry.target);
        }
      });
    },
    {
      root: document.querySelector(grid.value.el),
      rootMargin: '0px',
    },
  );
  slides.forEach((slide) => observer.observe(slide));
}

onMounted(() => {
  if (isMobile.value && currentPagination.value) {
    window.addEventListener('scroll', handleScroll);
  }

  gridItemsLength.value = grid.value.querySelectorAll(GRIDT_ITEM_SELECTOR).length;
});
</script>

<style lang="scss">
@import '../../styles/style.scss';
@import '../productCard/style.scss';
@import './style.scss';
</style>
